import { ApolloLink, Observable } from '@apollo/client'
import { getMainDefinition } from '@apollo/client/utilities/index.js'
import { buildTraceparent, randomHex } from './client-spans'

type InFlightListener = (count: number) => void

let inFlightOperationCount = 0
const listeners = new Set<InFlightListener>()

const setInFlightCount = (next: number): void => {
  inFlightOperationCount = next
  for (const listener of listeners) {
    listener(next)
  }
}

export const getInFlightOperationCount = (): number => inFlightOperationCount

export const subscribeToInFlightOperations = (listener: InFlightListener): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

// W3C trace context header: version 00, 16-byte trace id, 8-byte parent span id, sampled flag.
// The server's OTel HTTP instrumentation parents its trace to this id, linking a slow browser
// fetch to its Cloud Trace server trace.
export const createTraceparent = (): string => buildTraceparent(randomHex(16), randomHex(8))

/** The 32-hex trace id of a well-formed traceparent header, for log/trace joins. */
export const traceIdOfTraceparent = (traceparent: string): string => traceparent.split('-')[1]

export const timingLink = new ApolloLink((operation, forward) => {
  const definition = getMainDefinition(operation.query)
  const isSubscription =
    definition.kind === 'OperationDefinition' && definition.operation === 'subscription'

  // Subscriptions are long-lived and ride the websocket link; they are not page-load fetches.
  if (isSubscription) {
    return forward(operation)
  }

  // Callers that already minted a trace (e.g. a guest frame load parenting its queries under one
  // frame trace) pass `context.traceparent`; everything else gets a fresh per-operation trace.
  operation.setContext(
    ({
      headers = {},
      traceparent,
    }: {
      headers?: Record<string, string>
      traceparent?: string
    }) => ({
      headers: { ...headers, traceparent: traceparent ?? createTraceparent() },
    }),
  )

  setInFlightCount(inFlightOperationCount + 1)
  let settled = false
  const settle = (): void => {
    if (!settled) {
      settled = true
      setInFlightCount(inFlightOperationCount - 1)
    }
  }

  return new Observable((observer) => {
    const subscription = forward(operation).subscribe({
      next: (value) => observer.next(value),
      error: (error) => {
        settle()
        observer.error(error)
      },
      complete: () => {
        settle()
        observer.complete()
      },
    })

    return () => {
      // Teardown before completion (component unmount / cancelled query) must still settle the
      // counter, otherwise the page would never reach "network quiet".
      settle()
      subscription.unsubscribe()
    }
  })
})
